<script setup>
import { onMounted, h, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import { Head, router, usePage } from '@inertiajs/vue3'
import { Flasher, Toaster, useToast, ConfirmDialog, Button } from '@codinglabsau/gooey'
import { InfoIcon } from 'lucide-vue-next'
import { useHeaderBanners } from '@/Composables/useHeaderBanners'
import { useHeaderCounters } from '@/Composables/useHeaderCounters'
import { echo, useEcho, useEchoPublic, useEchoNotification } from '@laravel/echo-vue'
import { userChannel, notInPrivateChannel, tenantChannel } from '@/Composables/useBroadcasting'
import LocaleModal from '@/Components/Modals/LocaleModal.vue'
import { useLocaleModal } from '@/Composables/useLocaleModal.js'

const props = defineProps({
  title: String,
  broadcasting: {
    type: Boolean,
    default: () => true,
  },
})

const { showLocaleModal, closeLocaleModal, openLocaleModal } = useLocaleModal()

const autoCurrency = computed(() => usePage().props.session?.auto_currency)

const { toast } = useToast()
const { updateBanner } = useHeaderBanners()
const { incrementCounter, decrementCounter } = useHeaderCounters()

// flash notifications do not have a corresponding database notification
const flashNotificationsTypes = ['message_created']

// prevent joining channels in landlord environment
if (props.broadcasting) {
  // prevent duplicate joining
  if (usePage().props.auth?.user && notInPrivateChannel(userChannel())) {
    useEchoNotification(
      userChannel(),
      (data) => {
        toast({
          icon: data?.image,
          title: data.heading,
          description: data.message,
          meta: data,
        })

        if (!flashNotificationsTypes.includes(data.type)) {
          incrementCounter('notifications')
        }
      })

    useEcho(
      userChannel(),
      '.notifications.deleted',
      (data) => decrementCounter('notifications', data.count),
    )
  }

  useEchoPublic(
    tenantChannel('global'),
    '.refresh-live-event-header',
    (data) => updateBanner('live-event', data),
  )
}

onMounted(() => {
  router.on('before', (event) => {
    const socketId = echo().socketId()
    const isValidSocketId = /^\d+\.\d+$/.test(socketId)

    if (socketId && isValidSocketId) {
      event.detail.visit.headers['X-Socket-ID'] = socketId
    }
  })

  if (usePage().props.auth?.user) {
    Sentry.setUser({
      username: usePage().props.auth.user.nick,
    })
  }

  // Show toast for auto-detected currency with a Change action
  if (autoCurrency.value) {
    const ChangeAction = {
      name: 'ChangeCurrencyAction',
      setup() {
        return () => h(
          Button,
          {
            type: 'button',
            variant: 'outline',
            onClick: () => {
              openLocaleModal()
            },
          },
          __('Change'),
        )
      },
    }

    toast({
      icon: InfoIcon,
      iconClasses: 'text-blue-500',
      title: __('Heads up!'),
      description: autoCurrency.value.message || __('We\'ve set your currency to :currency.', { currency: autoCurrency.value.code }),
      action: ChangeAction,
    })
  }
})

const onClickToast = (toast) => {
  if (!toast.meta) {
    return
  }

  if (flashNotificationsTypes.includes(toast.meta.type)) {
    toast.open = false

    toast.meta.inertia
      ? router.get(toast.meta.url)
      : window.location = toast.meta.url
    return
  }

  router.post(route('account.notifications.read', toast.meta.id))
}
</script>

<template>
  <Head :title="title" />

  <div>
    <Flasher
      :info="$page.props.info"
      :success="$page.props.success"
      :warning="$page.props.warning"
      :errors="$page.props.errors"
    />

    <ConfirmDialog />

    <Toaster @click="onClickToast" />

    <div>
      <slot />
    </div>

    <keep-alive>
      <component
        v-bind="$page.props"
        :is="$root.modalComponent"
        v-if="$root.modalComponent"
        :key="$page.props.modal"
      />
    </keep-alive>

    <modals-container />

    <LocaleModal @close="closeLocaleModal()" />
  </div>
</template>
